import React from "react";
import { useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";

const LEGAL_ENTITY = "**Legal entity.** Niraj Bhaardwaj Academy is a subsidiary of **Bhaardwaj's Electronics's** (GST No. **03DOFPK8846Q1Z7**). All operations, invoicing, payments, refunds, and legal correspondence for this platform are handled exclusively by Bhaardwaj's Electronics's.";

const CONTENT = {
  terms: {
    title: "Terms & Conditions",
    eyebrow: "Legal",
    body: [
      "Welcome to Niraj Bhaardwaj Academy. By accessing this website or purchasing any course, service, or programme, you agree to be bound by these Terms & Conditions.",
      LEGAL_ENTITY,
      "**1. Use of the platform.** You must be 18 years or older, or have permission from a parent/guardian, to purchase or access our courses.",
      "**2. Account security.** You are responsible for keeping your login credentials confidential and for any activity on your account.",
      "**3. Intellectual property.** All content on this platform — videos, workbooks, prompts, templates, artwork, brand assets — is the property of Niraj Bhaardwaj and is protected by copyright. You may not copy, redistribute, resell, or share the course material with anyone.",
      "**4. Course access.** Once enrolled, you receive lifetime access to the purchased course on this platform, subject to the platform continuing to operate. Account sharing is strictly prohibited and may result in immediate suspension without refund.",
      "**5. No income guarantees.** Any references to student outcomes, revenue, or business results are aspirational and depend on your own effort, market, and circumstances. We make no guarantees of financial return.",
      "**6. Changes.** We may update these Terms from time to time. Continued use after changes constitutes acceptance.",
      "**7. Contact.** For any questions, write to us via the contact page.",
    ],
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Legal",
    body: [
      "This Privacy Policy explains how Niraj Bhaardwaj Academy collects, uses, and protects your personal information.",
      LEGAL_ENTITY,
      "**1. What we collect.** Name, email, phone number, payment details (via our payment processor — never stored on our servers), and behavioural data such as course progress and sign-in history.",
      "**2. Why we collect it.** To deliver purchased courses, communicate updates, provide customer support, secure your account, and improve the learning experience.",
      "**3. Who we share it with.** We do NOT sell your data. We share only with essential service providers (payment gateway, email service, hosting) and only what they need to deliver their service.",
      "**4. Cookies.** We use cookies for authentication (keeping you signed in) and basic analytics. You can disable cookies in your browser, though some features may stop working.",
      "**5. Data security.** Passwords are stored as bcrypt hashes. Payments are handled by PCI-compliant processors. Sign-in history is available to you inside your dashboard.",
      "**6. Your rights.** You can request access to, correction of, or deletion of your personal data at any time by writing to us via the contact page.",
      "**7. Updates.** This policy may be updated. The latest version is always available on this page.",
    ],
  },
  refund: {
    title: "Refund Policy",
    eyebrow: "Legal",
    body: [
      "**All sales are final. There are no refunds** for any course, service, workshop, or programme purchased on this platform.",
      LEGAL_ENTITY,
      "By completing a purchase, you acknowledge that you have reviewed the course description, curriculum, and pricing, and you agree that no refund will be issued once payment is made — regardless of whether the course has been accessed, partially consumed, or fully completed.",
      "**Why no refunds?** All content is delivered digitally and is immediately accessible upon enrolment. Once granted, access cannot be revoked or 'returned'.",
      "**Exceptions.** We only issue a refund if we cannot deliver the course due to a technical failure on our side that remains unresolved. In such rare cases, please contact us via the contact page and we will review your case.",
      "**Chargebacks.** Filing a chargeback for a completed purchase covered by this no-refund policy will result in immediate loss of course access and permanent account suspension.",
      "By purchasing, you accept this No-Refund policy in full.",
    ],
  },
};

function renderBlock(text, i) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <p key={i} className="mt-6 text-ink-700 leading-relaxed">
      {parts.map((p, j) => (j % 2 === 1 ? <strong key={j} className="text-ink-900">{p}</strong> : <React.Fragment key={j}>{p}</React.Fragment>))}
    </p>
  );
}

export default function Legal() {
  const { slug } = useParams();
  const c = CONTENT[slug];
  if (!c) return <PublicLayout><div className="container-x py-24">Not found</div></PublicLayout>;
  return (
    <PublicLayout>
      <section className="container-x pt-16 pb-24 max-w-3xl" data-testid={`legal-${slug}`}>
        <div className="overline mb-4">{c.eyebrow}</div>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-ink-900">{c.title}</h1>
        <div className="mt-4 text-xs text-ink-500">Last updated: February 2026</div>
        <div className="mt-10">
          {c.body.map(renderBlock)}
        </div>
      </section>
    </PublicLayout>
  );
}
